import React from 'react';
import PropTypes from 'prop-types';
import Button from 'react-bootstrap/lib/Button';
import Col from 'react-bootstrap/lib/Col';
import Row from 'react-bootstrap/lib/Row';
import { Coins } from 'lucide-react';

import ButtonWithLoader from 'components/ButtonWithLoader/ButtonWithLoader';
import {
  APPLICATION_CREDITS_BUNDLE_SIZE,
  APPLICATION_CREDITS_EXPIRY_DAYS,
} from 'helpers/ApplicationCreditsHelper';

import styles from './PricingBreakdownCreative.module.scss';

const PAYG_FEATURES = [
  {
    title: `${APPLICATION_CREDITS_BUNDLE_SIZE} application credits`,
    description: `A one-off pack of ${APPLICATION_CREDITS_BUNDLE_SIZE} credits to apply when you need them.`,
  },
  {
    title: '1 credit per application',
    description:
      'Each successful application uses one credit from your balance.',
  },
  {
    title: 'Apply to <b>any</b> job',
    description: 'Credits work on any job, including popular listings.',
  },
  {
    title: 'Credits last 1 month',
    description: `Unused credits expire ${APPLICATION_CREDITS_EXPIRY_DAYS} days after purchase.`,
  },
  {
    title: 'Only spent on submit',
    description:
      'Credits are only used when you successfully submit an application.',
  },
];

const withVisibility = (className, visible, end = false) =>
  className +
  (visible ? '' : ' ' + styles.hide) +
  (end ? ' ' + styles.end : '');

const PaygPricingSection = ({
  visible,
  currency,
  applicationCreditsPrice,
  applicationCreditsPerApplication,
  proAnnualPerApplication,
  proAnnualSavingsMultiplier,
  loadingApplicationCreditsCheckout,
  loadingPro,
  loadingPremium,
  submitLoadingText,
  onBuyCredits,
  onSeeAnnualPlans,
  renderFeatureRow,
}) => (
  <Row>
    <Col
      xs={12}
      md={6}
      className={withVisibility(
        styles.standardSection + ' ' + styles.paygSection + ' box-shadow',
        visible
      )}
    >
      <div className={styles.paygBadge}>
        <Coins size={14} className={styles.paygBadgeIcon} />
        {APPLICATION_CREDITS_BUNDLE_SIZE} credits
      </div>
      <Row>
        <h3
          className={withVisibility(
            styles.header + ' ' + styles.paygHeader,
            visible
          )}
        >
          Pay As You Go
        </h3>
      </Row>
      <Row
        className={withVisibility(
          styles.section +
            ' ' +
            styles.priceSection +
            ' ' +
            styles.paygPriceSection,
          visible
        )}
      >
        <p>
          <span className="currency-symbol">{currency.value}</span>
          <span className={styles.price} id="application-credits-price">
            {applicationCreditsPrice}
          </span>{' '}
          <span className={styles.billingPeriodLabel}>one-off</span>
        </p>
        <p className={styles.paygPerApplication}>
          {currency.value}
          {applicationCreditsPerApplication} per application - no subscription
        </p>
      </Row>
      <Row
        className={withVisibility(
          styles.section +
            ' ' +
            styles.buttonSection +
            ' ' +
            styles.paygButtonSection,
          visible
        )}
      >
        <ButtonWithLoader
          loading={loadingApplicationCreditsCheckout}
          disabled={
            loadingPro || loadingPremium || loadingApplicationCreditsCheckout
          }
          onClick={onBuyCredits}
          className="btn-lg btn-block"
          bsStyle="hollow"
          id="buy-application-credits-button"
          text={
            loadingApplicationCreditsCheckout
              ? submitLoadingText
              : `Buy ${APPLICATION_CREDITS_BUNDLE_SIZE} credits`
          }
          success={false}
        />
      </Row>
      {PAYG_FEATURES.map((feature, index) => (
        <Row
          key={feature.title}
          className={withVisibility(
            styles.section,
            visible,
            index === PAYG_FEATURES.length - 1
          )}
        >
          {renderFeatureRow(feature.title, feature.description)}
        </Row>
      ))}
    </Col>
    <Col xs={12} md={6}>
      <div
        className={withVisibility(
          styles.proSection + ' ' + styles.betterValueSection + ' box-shadow',
          visible
        )}
      >
        <Row className={styles.rowHeader}>
          <h3
            className={withVisibility(
              styles.header + ' ' + styles.pro,
              visible
            )}
          >
            Better value
          </h3>
        </Row>
        <Row
          className={withVisibility(
            styles.section + ' ' + styles.betterValueBody,
            visible
          )}
        >
          <p className={styles.betterValueIntro}>
            Applying often? Pro Annual works out far cheaper.
          </p>
          <div className={styles.betterValueComparison}>
            <div className={styles.betterValueRow}>
              <span>Pay As You Go</span>
              <span>
                {currency.value}
                {applicationCreditsPerApplication} / application
              </span>
            </div>
            <div
              className={
                styles.betterValueRow + ' ' + styles.betterValueHighlight
              }
            >
              <span>Pro Annual</span>
              <span>
                {currency.value}
                {proAnnualPerApplication} / application
              </span>
            </div>
          </div>
          <p className={styles.betterValueFooter}>
            That&apos;s roughly <b>{proAnnualSavingsMultiplier}x less</b> per
            application, plus visibility, no fees, and the AI cover-letter
            writer.
          </p>
        </Row>
        <Row
          className={withVisibility(
            styles.section + ' ' + styles.betterValueButtonSection,
            visible,
            true
          )}
        >
          <Button
            bsStyle="primary"
            className="btn-lg btn-block"
            onClick={onSeeAnnualPlans}
          >
            See Annual plans
          </Button>
        </Row>
      </div>
    </Col>
  </Row>
);

PaygPricingSection.propTypes = {
  visible: PropTypes.bool.isRequired,
  currency: PropTypes.shape({
    value: PropTypes.string.isRequired,
  }).isRequired,
  applicationCreditsPrice: PropTypes.string.isRequired,
  applicationCreditsPerApplication: PropTypes.string.isRequired,
  proAnnualPerApplication: PropTypes.string.isRequired,
  proAnnualSavingsMultiplier: PropTypes.number.isRequired,
  loadingApplicationCreditsCheckout: PropTypes.bool.isRequired,
  loadingPro: PropTypes.bool.isRequired,
  loadingPremium: PropTypes.bool.isRequired,
  submitLoadingText: PropTypes.string,
  onBuyCredits: PropTypes.func.isRequired,
  onSeeAnnualPlans: PropTypes.func.isRequired,
  renderFeatureRow: PropTypes.func.isRequired,
};

export default PaygPricingSection;
